import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add, Search } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'

import { useGetInternalOrganizationsQuery } from '../../api/organizations'
import { useAddInternalPermissionsMutation, useGetInternalPermissionsQuery, useRemoveInternalPermissionsMutation } from '../../api/permissions'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { InternalPermissionType } from '../../types/InternalPermission'
import { InternalFormDialog } from '../InternalFormDialog/InternalFormDialog'
import {
  diffAccess,
  getGrantedAccess,
  getOrganizationLabel,
  getUnappliedAccess,
  MAX_ORGANIZATION_SEARCH_RESULTS,
  searchOrganizations,
} from './organizationAccessHelpers'

type OrganizationAccessDialogProps = {
  open: boolean
  type: InternalPermissionType
  itemId: string
  /** The item's name, shown in the title. */
  itemName: string
  /** Shown above the organizations, e.g. the item's image. */
  target?: ReactNode
  onClose: () => void
}

export const OrganizationAccessDialog: FC<OrganizationAccessDialogProps> = ({ open, type, itemId, itemName, target, onClose }) => {
  const { t } = useTranslation()
  const { notifySuccess } = useMutationFeedback()
  const organizationsQuery = useGetInternalOrganizationsQuery(undefined, { skip: !open })
  const permissionsQuery = useGetInternalPermissionsQuery(type, { skip: !open })
  const [addPermissions] = useAddInternalPermissionsMutation()
  const [removePermissions] = useRemoveInternalPermissionsMutation()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [isApplying, setIsApplying] = useState(false)
  const [unapplied, setUnapplied] = useState<{ notAdded: string[]; notRemoved: string[] }>()
  const [applyFailed, setApplyFailed] = useState(false)

  const organizations = useMemo(() => organizationsQuery.data ?? [], [organizationsQuery.data])
  const permission = permissionsQuery.data?.find(({ id }) => id === itemId)
  const { grantedIds, staleCount } = useMemo(() => getGrantedAccess(permission, organizations), [permission, organizations])
  const isLoaded = organizationsQuery.isSuccess && permissionsQuery.isSuccess
  const organizationName = (id: string) =>
    getOrganizationLabel(
      organizations.find((organization) => organization.id === id),
      id
    )
  const matches = useMemo(() => searchOrganizations(organizations, search, selectedIds), [organizations, search, selectedIds])
  const { added, removed } = diffAccess(grantedIds, selectedIds)
  const hasChanges = added.length > 0 || removed.length > 0

  useEffect(() => {
    if (!open) return
    setSearch('')
    setUnapplied(undefined)
    setApplyFailed(false)
  }, [open, itemId])

  useEffect(() => {
    if (open && isLoaded) setSelectedIds(grantedIds)
  }, [open, isLoaded, grantedIds])

  const apply = async () => {
    setIsApplying(true)
    setUnapplied(undefined)
    setApplyFailed(false)
    let failed = false
    try {
      if (removed.length) await removePermissions({ type, itemId, organizationIds: removed }).unwrap()
      if (added.length) await addPermissions({ type, itemId, organizationIds: added }).unwrap()
    } catch {
      failed = true
    }
    // Read back even after a failure: part of the change may have been stored.
    const stored = await permissionsQuery.refetch()
    setIsApplying(false)
    if (stored.isError || !stored.data) {
      setApplyFailed(true)
      return
    }
    const result = getUnappliedAccess(
      stored.data.find(({ id }) => id === itemId),
      added,
      removed
    )
    if (result.notAdded.length || result.notRemoved.length) {
      setUnapplied(result)
      return
    }
    if (failed) {
      setApplyFailed(true)
      return
    }
    notifySuccess(t('internal-common:organization_access_saved'))
    onClose()
  }

  return (
    <InternalFormDialog
      open={open}
      title={t('internal-common:organization_access_title', { name: itemName })}
      onClose={onClose}
      onSave={apply}
      saveDisabled={!isLoaded || !hasChanges}
      isSaving={isApplying}
      saveLabel={t('internal-common:organization_access_apply')}
      maxWidth="md"
    >
      <Stack spacing={2} pt={1}>
        {target}
        {organizationsQuery.isError || permissionsQuery.isError ? (
          <Alert severity="error">{t('internal-common:organization_access_load_failed')}</Alert>
        ) : !isLoaded ? (
          <Skeleton variant="rounded" height={120} />
        ) : (
          <>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('internal-common:organization_access_granted', { count: selectedIds.length })}
              </Typography>
              {selectedIds.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('internal-common:organization_access_none')}
                </Typography>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {selectedIds.map((id) => (
                    <Chip
                      key={id}
                      label={organizationName(id)}
                      color={added.includes(id) ? 'primary' : 'default'}
                      onDelete={() => setSelectedIds((current) => current.filter((selectedId) => selectedId !== id))}
                      disabled={isApplying}
                    />
                  ))}
                </Stack>
              )}
              {staleCount > 0 && (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {t('internal-common:organization_access_stale', { count: staleCount })}
                </Typography>
              )}
            </Box>
            <TextField
              size="small"
              fullWidth
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('internal-common:organization_access_search')}
              inputProps={{ 'aria-label': t('internal-common:organization_access_search') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            {search.trim() && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('internal-common:organization_access_name')}</TableCell>
                      <TableCell>{t('internal-common:organization_access_contact_email')}</TableCell>
                      <TableCell align="right">{t('internal-common:organization_access_users')}</TableCell>
                      <TableCell sx={{ width: 100 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            {t('internal-common:organization_access_no_matches')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      matches.slice(0, MAX_ORGANIZATION_SEARCH_RESULTS).map((organization) => (
                        <TableRow key={organization.id} hover>
                          <TableCell>{organization.name?.trim() || '–'}</TableCell>
                          <TableCell>{organization.contactEmail || '–'}</TableCell>
                          <TableCell align="right">{organization.userCount ?? 0}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              startIcon={<Add />}
                              onClick={() => setSelectedIds((current) => [...current, organization.id])}
                              disabled={isApplying}
                              aria-label={t('internal-common:organization_access_add_named', { name: getOrganizationLabel(organization, organization.id) })}
                            >
                              {t('internal-common:organization_access_add')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                {matches.length > MAX_ORGANIZATION_SEARCH_RESULTS && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {t('internal-common:organization_access_more_matches', { count: matches.length - MAX_ORGANIZATION_SEARCH_RESULTS })}
                  </Typography>
                )}
              </TableContainer>
            )}
          </>
        )}
        {unapplied && (
          <Alert severity="error">
            {[
              unapplied.notAdded.length > 0 &&
                t('internal-common:organization_access_not_added', { names: unapplied.notAdded.map(organizationName).join(', ') }),
              unapplied.notRemoved.length > 0 &&
                t('internal-common:organization_access_not_removed', { names: unapplied.notRemoved.map(organizationName).join(', ') }),
            ]
              .filter(Boolean)
              .join(' ')}
          </Alert>
        )}
        {applyFailed && <Alert severity="error">{t('internal-common:organization_access_apply_failed')}</Alert>}
      </Stack>
    </InternalFormDialog>
  )
}
